
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EpisodesManager } from "./EpisodesManager";
import { ActivitiesManager } from "./ActivitiesManager";
import { PartnersManager } from "./PartnersManager";
import { SiteSettingsManager } from "./SiteSettingsManager";
import { CategoriesManager } from "./CategoriesManager";
import { CharactersManager } from "./CharactersManager";
import { CharacterOptionsManager } from "./characters/CharacterOptionsManager";
import { OrphanFileCleanup } from "./OrphanFileCleanup";
import { SitemapManager } from "./SitemapManager";
import { MetricCard } from "./MetricCard";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import {
  Play,
  Puzzle,
  Users,
  BarChart,
  Settings,
  Tag,
  User,
  Trash,
  UserCog,
  Globe,
} from "lucide-react";

export const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get initial tab from URL hash, default to "overview"
  const getInitialTab = () => {
    const hash = location.hash.replace('#', '');
    const validTabs = [
      "overview", "episodes", "characters", "character-options", 
      "activities", "partners", "categories", "cleanup", "sitemap", "settings"
    ];
    return validTabs.includes(hash) ? hash : "overview";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const { data: metrics, isLoading, error } = useDashboardMetrics();

  // Update URL hash when active tab changes
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    navigate(`/admin#${newTab}`, { replace: true });
  };

  // Listen to hash changes (back/forward navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const newTab = getInitialTab();
      setActiveTab(newTab);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [location.hash]);

  // Update tab when location changes (initial load or direct navigation)
  useEffect(() => {
    const newTab = getInitialTab();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.hash]);

  return (
    <div className="admin-container space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-teedee-blue">
          Dashboard
        </h1>
        <p className="text-gray-600 mt-2 text-sm md:text-base">
          Manage your Bobo & Teedee content and settings
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4 md:space-y-6"
      >
        <TabsList className="admin-tabs-list h-auto p-1">
          <TabsTrigger value="overview" className="admin-tab-trigger">
            <BarChart className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden xs:inline ml-1 md:ml-2">Overview</span>
            <span className="xs:hidden ml-1">Stats</span>
          </TabsTrigger>
          <TabsTrigger value="episodes" className="admin-tab-trigger">
            <Play className="h-3 w-3 md:h-4 md:w-4" />
            <span className="ml-1 md:ml-2">Episodes</span>
          </TabsTrigger>
          <TabsTrigger value="characters" className="admin-tab-trigger">
            <User className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden xs:inline ml-1 md:ml-2">Characters</span>
            <span className="xs:hidden ml-1">Chars</span>
          </TabsTrigger>
          <TabsTrigger value="character-options" className="admin-tab-trigger">
            <UserCog className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden xs:inline ml-1 md:ml-2">Char Options</span>
            <span className="xs:hidden ml-1">Opts</span>
          </TabsTrigger>
          <TabsTrigger value="activities" className="admin-tab-trigger">
            <Puzzle className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden xs:inline ml-1 md:ml-2">Activities</span>
            <span className="xs:hidden ml-1">Acts</span>
          </TabsTrigger>
          <TabsTrigger value="partners" className="admin-tab-trigger">
            <Users className="h-3 w-3 md:h-4 md:w-4" />
            <span className="ml-1 md:ml-2">Partners</span>
          </TabsTrigger>
          <TabsTrigger value="categories" className="admin-tab-trigger">
            <Tag className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden xs:inline ml-1 md:ml-2">Categories</span>
            <span className="xs:hidden ml-1">Cats</span>
          </TabsTrigger>
          <TabsTrigger value="cleanup" className="admin-tab-trigger">
            <Trash className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden xs:inline ml-1 md:ml-2">Cleanup</span>
            <span className="xs:hidden ml-1">Clean</span>
          </TabsTrigger>
          <TabsTrigger value="sitemap" className="admin-tab-trigger">
            <Globe className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden xs:inline ml-1 md:ml-2">Sitemap</span>
            <span className="xs:hidden ml-1">Map</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="admin-tab-trigger">
            <Settings className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden xs:inline ml-1 md:ml-2">Settings</span>
            <span className="xs:hidden ml-1">Set</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 md:space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">
                Failed to load dashboard metrics. Please try refreshing the
                page.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <MetricCard
              title="Total Episodes"
              value={metrics?.totalEpisodes || 0}
              recentChange={metrics?.recentEpisodes || 0}
              icon={Play}
              isLoading={isLoading}
              onClick={() => handleTabChange("episodes")}
            />
            <MetricCard
              title="Characters"
              value={metrics?.totalCharacters || 0}
              recentChange={metrics?.recentCharacters || 0}
              icon={User}
              isLoading={isLoading}
              onClick={() => handleTabChange("characters")}
            />
            <MetricCard
              title="Activities"
              value={metrics?.totalActivities || 0}
              recentChange={metrics?.recentActivities || 0}
              icon={Puzzle}
              isLoading={isLoading}
              onClick={() => handleTabChange("activities")}
            />
            <MetricCard
              title="Partners"
              value={metrics?.totalPartners || 0}
              recentChange={metrics?.recentPartners || 0}
              icon={Users}
              isLoading={isLoading}
              onClick={() => handleTabChange("partners")}
            />
          </div>
        </TabsContent>

        <TabsContent value="episodes" className="admin-container">
          <EpisodesManager />
        </TabsContent>

        <TabsContent value="characters" className="admin-container">
          <CharactersManager />
        </TabsContent>

        <TabsContent value="character-options" className="admin-container">
          <CharacterOptionsManager />
        </TabsContent>

        <TabsContent value="categories" className="admin-container">
          <CategoriesManager />
        </TabsContent>

        <TabsContent value="activities" className="admin-container">
          <ActivitiesManager />
        </TabsContent>

        <TabsContent value="partners" className="admin-container">
          <PartnersManager />
        </TabsContent>

        <TabsContent value="cleanup" className="admin-container">
          <OrphanFileCleanup />
        </TabsContent>

        <TabsContent value="sitemap" className="admin-container">
          <SitemapManager />
        </TabsContent>

        <TabsContent value="settings" className="admin-container">
          <SiteSettingsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};
